import DOMPurify from 'dompurify'

/**
 * Sanitize HTML content using DOMPurify
 * @param html - HTML string to sanitize
 * @returns Sanitized HTML string
 */
export function sanitizeHtml(html: string): string {
  return DOMPurify.sanitize(html || '')
}

/**
 * Format LLM-generated text that contains markdown-like formatting
 * Converts markdown-style formatting to HTML:
 * - `\n\n` → paragraph breaks
 * - `**text**` → bold
 * - Numbered lists (`1.`, `2.`, etc.) → ordered list
 * - Bullet points (`-`, `•`) → unordered list
 *
 * @param text - Text content with markdown-like formatting
 * @returns Formatted HTML string (sanitized)
 */
export function formatLlmText(text: string): string {
  if (!text) return ''

  let formatted = text

  // Handle numbered lists (1., 2., 3., etc.)
  // Match lines that start with a number followed by a period and space
  const numberedListRegex = /^(\d+)\.\s+(.+)$/gm
  const numberedMatches: Array<{ full: string; number: string; content: string }> = []
  let match
  while ((match = numberedListRegex.exec(formatted)) !== null) {
    numberedMatches.push({
      full: match[0],
      number: match[1],
      content: match[2]
    })
  }

  // If we found numbered list items, wrap them in <ol>
  if (numberedMatches.length > 0) {
    // Replace numbered list items with <li> tags
    numberedMatches.forEach((item) => {
      formatted = formatted.replace(item.full, `<li>${item.content}</li>`)
    })
    // Find the first and last occurrence to wrap in <ol>
    const firstIndex = formatted.indexOf('<li>')
    const lastIndex = formatted.lastIndexOf('</li>')
    if (firstIndex !== -1 && lastIndex !== -1) {
      const before = formatted.substring(0, firstIndex)
      const listContent = formatted.substring(firstIndex, lastIndex + 5)
      const after = formatted.substring(lastIndex + 5)
      formatted = `${before}<ol>${listContent}</ol>${after}`
    }
  }

  // Handle bullet points (- or •)
  const bulletRegex = /^[-•]\s+(.+)$/gm
  const bulletMatches: Array<{ full: string; content: string }> = []
  while ((match = bulletRegex.exec(formatted)) !== null) {
    bulletMatches.push({
      full: match[0],
      content: match[1]
    })
  }

  // If we found bullet points, wrap them in <ul>
  if (bulletMatches.length > 0) {
    bulletMatches.forEach((item) => {
      formatted = formatted.replace(item.full, `<li>${item.content}</li>`)
    })
    // Find the first and last occurrence to wrap in <ul>
    const firstIndex = formatted.indexOf('<li>')
    const lastIndex = formatted.lastIndexOf('</li>')
    if (firstIndex !== -1 && lastIndex !== -1 && !formatted.includes('<ol>')) {
      const before = formatted.substring(0, firstIndex)
      const listContent = formatted.substring(firstIndex, lastIndex + 5)
      const after = formatted.substring(lastIndex + 5)
      formatted = `${before}<ul>${listContent}</ul>${after}`
    }
  }

  // Replace markdown bold **text** → <strong>text</strong>
  formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')

  // Handle paragraph breaks: \n\n → </p><p>
  // First, wrap the entire content in <p> tags if it's not already in a list
  if (!formatted.includes('<ol>') && !formatted.includes('<ul>')) {
    // Split by double newlines and wrap each paragraph
    const paragraphs = formatted.split(/\n\n+/)
    formatted = paragraphs
      .map((p) => p.trim())
      .filter((p) => p.length > 0)
      .map((p) => `<p>${p}</p>`)
      .join('')
  } else {
    // If we have lists, handle newlines more carefully
    // Replace \n\n with <br/><br/> within paragraphs, but preserve list structure
    formatted = formatted.replace(/\n\n+/g, '<br/><br/>')
  }

  // Replace single newlines with <br/> (but preserve list structure)
  // Split by lines and process carefully to avoid breaking lists
  const lines = formatted.split('\n')
  const processedLines: string[] = []
  let inList = false

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    const isListItem = line.trim().startsWith('<li>') || line.trim().startsWith('</li>')

    if (isListItem) {
      inList = true
      processedLines.push(line)
    } else if (line.trim() === '' && inList) {
      // Empty line in list - might be end of list
      inList = false
      processedLines.push(line)
    } else if (line.trim() !== '' && !inList) {
      // Regular line outside list - replace newline with <br/>
      processedLines.push(line + '<br/>')
    } else {
      processedLines.push(line)
    }
  }

  formatted = processedLines.join('\n')

  // Final cleanup: remove trailing <br/> before closing tags
  formatted = formatted.replace(/<br\/>\s*(<\/[ou]l>|<\/p>|<\/ol>)/g, '$1')

  // Sanitize the final HTML
  return sanitizeHtml(formatted)
}

/**
 * Parse markdown to HTML (more comprehensive parser)
 * This is a basic implementation - for full markdown support, consider using a library
 *
 * @param markdown - Markdown text
 * @returns HTML string (sanitized)
 */
export function parseMarkdownToHtml(markdown: string): string {
  if (!markdown) return ''

  let html = markdown

  // Headers
  html = html.replace(/^### (.+)$/gm, '<h3>$1</h3>')
  html = html.replace(/^## (.+)$/gm, '<h2>$1</h2>')
  html = html.replace(/^# (.+)$/gm, '<h1>$1</h1>')

  // Bold
  html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
  html = html.replace(/__(.+?)__/g, '<strong>$1</strong>')

  // Italic
  html = html.replace(/\*(.+?)\*/g, '<em>$1</em>')
  html = html.replace(/_(.+?)_/g, '<em>$1</em>')

  // Code blocks
  html = html.replace(/```([\s\S]*?)```/g, '<pre><code>$1</code></pre>')
  html = html.replace(/`(.+?)`/g, '<code>$1</code>')

  // Links
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>')

  // Use formatLlmText for lists and paragraphs
  html = formatLlmText(html)

  return sanitizeHtml(html)
}
